import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ECOSYSTEM_EVENT_KIND_ORDER,
  getActiveUpcomingXrpLedgerEvents,
  groupXrpLedgerEventsByMonth,
  upcomingXrpLedgerEvents,
  type EcosystemEventKind,
  type UpcomingXrpLedgerEvent,
} from "../data/upcomingXrpLedgerEvents";
import EcosystemEventModal from "./EcosystemEventModal";
import "./UpcomingXrpLedgerEvents.css";

type UpcomingXrpLedgerEventsProps = {
  events?: UpcomingXrpLedgerEvent[];
  upcomingTitle?: string;
  upcomingHeadingId?: string;
};

type SortMode = "date" | EcosystemEventKind;
type LayoutMode = "grid" | "list";

const LAYOUT_STORAGE_KEY = "woi-ecosystem-events-layout-v2";
const MOBILE_LAYOUT_QUERY = "(max-width: 640px)";
const ADD_EVENT_MAILTO = "mailto:events@waveofinnovation.com?subject=Add%20an%20ecosystem%20event";

function isMobileLayout(): boolean {
  return typeof window !== "undefined" && window.matchMedia(MOBILE_LAYOUT_QUERY).matches;
}

function readStoredLayout(): LayoutMode | null {
  try {
    const stored = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (stored === "list" || stored === "grid") return stored;
  } catch {
    /* ignore */
  }
  return null;
}

function getDefaultLayout(): LayoutMode {
  if (typeof window === "undefined") return "grid";
  if (isMobileLayout()) return "list";
  return readStoredLayout() ?? "grid";
}

function isExternalHref(event: UpcomingXrpLedgerEvent): boolean {
  if (typeof event.external === "boolean") return event.external;
  return Boolean(event.href && /^https?:\/\//i.test(event.href));
}

function EventMeta({ event }: { event: UpcomingXrpLedgerEvent }) {
  return (
    <span className="upcoming-xrp-event-meta">
      <time dateTime={event.dateTime}>{event.dateLabel}</time>
      <span className="upcoming-xrp-event-sep" aria-hidden="true">
        ·
      </span>
      <span>{event.location}</span>
    </span>
  );
}

function EventCard({
  event,
  onOpen,
  isOpen,
}: {
  event: UpcomingXrpLedgerEvent;
  onOpen: (event: UpcomingXrpLedgerEvent) => void;
  isOpen?: boolean;
}) {
  const label = `${event.name} — ${event.dateLabel}, ${event.location}`;
  const className = `upcoming-xrp-event-card${event.href ? " upcoming-xrp-event-card--link" : ""}`;

  const body = (
    <>
      <div className="upcoming-xrp-event-card-media">
        <img
          src={event.image}
          alt={event.imageAlt}
          width={960}
          height={600}
          loading="lazy"
          decoding="async"
        />
        {event.kinds.length > 0 ? (
          <span className="upcoming-xrp-event-card-tags" aria-hidden="true">
            {event.kinds.map((kind) => (
              <span key={kind} className="upcoming-xrp-event-card-tag">
                {kind}
              </span>
            ))}
          </span>
        ) : null}
      </div>
      <div className="upcoming-xrp-event-card-copy">
        <span className="upcoming-xrp-event-name">{event.name}</span>
        <EventMeta event={event} />
      </div>
    </>
  );

  if (!event.href) {
    return <article className={className}>{body}</article>;
  }

  if (isExternalHref(event)) {
    return (
      <button
        type="button"
        className={`${className} upcoming-xrp-event-card--button`}
        onClick={() => onOpen(event)}
        aria-haspopup="dialog"
        aria-expanded={Boolean(isOpen)}
        aria-label={`${label} — view details`}
      >
        {body}
      </button>
    );
  }

  return (
    <Link to={event.href} className={className} aria-label={label}>
      {body}
    </Link>
  );
}

/**
 * Homepage XRP Ledger events band.
 * Groups upcoming tiles by month; dated-out events are not listed here.
 */
const UpcomingXrpLedgerEvents: React.FC<UpcomingXrpLedgerEventsProps> = ({
  events = upcomingXrpLedgerEvents,
  upcomingTitle = "Ecosystem XRP events",
  upcomingHeadingId = "upcoming-xrp-ledger-events-heading",
}) => {
  const [sortMode, setSortMode] = useState<SortMode>("date");
  const [sortOpen, setSortOpen] = useState(false);
  const [layout, setLayout] = useState<LayoutMode>(getDefaultLayout);
  const [selectedEvent, setSelectedEvent] = useState<UpcomingXrpLedgerEvent | null>(null);
  const sortRef = useRef<HTMLDivElement>(null);
  const closeEventModal = useCallback(() => setSelectedEvent(null), []);

  const selectLayout = (mode: LayoutMode) => {
    if (isMobileLayout()) return;
    setLayout(mode);
    try {
      window.localStorage.setItem(LAYOUT_STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const media = window.matchMedia(MOBILE_LAYOUT_QUERY);
    const syncLayout = () => {
      if (media.matches) {
        setLayout("list");
        return;
      }
      setLayout(readStoredLayout() ?? "grid");
    };
    syncLayout();
    media.addEventListener("change", syncLayout);
    return () => media.removeEventListener("change", syncLayout);
  }, []);

  useEffect(() => {
    if (!sortOpen) return undefined;
    const onPointerDown = (e: PointerEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setSortOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSortOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [sortOpen]);

  const upcoming = getActiveUpcomingXrpLedgerEvents(events);
  const visible =
    sortMode === "date" ? upcoming : upcoming.filter((event) => event.kinds.includes(sortMode));
  const months = groupXrpLedgerEventsByMonth(visible);

  if (upcoming.length === 0) return null;

  const selectSort = (mode: SortMode) => {
    setSortMode(mode);
    setSortOpen(false);
  };

  return (
    <section
      className={`upcoming-xrp-events${layout === "list" ? " upcoming-xrp-events--list" : ""}`}
      aria-labelledby={upcomingHeadingId}
    >
      <div className="upcoming-xrp-events-inner">
        <div className="upcoming-xrp-events-header">
          <div className="upcoming-xrp-events-heading-block">
            <h2 id={upcomingHeadingId} className="upcoming-xrp-events-title">
              {upcomingTitle}
            </h2>
            <p className="upcoming-xrp-events-intro">
              Ecosystem XRP events across the community (not all run by Wave of Innovation).
            </p>
            <a className="upcoming-xrp-events-add" href={ADD_EVENT_MAILTO}>
              Add your events
            </a>
          </div>
          <div className="upcoming-xrp-events-tools">
            <div className="upcoming-xrp-events-view" role="group" aria-label="Event layout">
              <button
                type="button"
                className={`upcoming-xrp-events-view-button${layout === "grid" ? " is-active" : ""}`}
                aria-pressed={layout === "grid"}
                aria-label="Grid view"
                onClick={() => selectLayout("grid")}
              >
                <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <path
                    d="M2.5 2.5h4.5v4.5H2.5V2.5zm6.5 0h4.5v4.5H9V2.5zM2.5 9h4.5v4.5H2.5V9zM9 9h4.5v4.5H9V9z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <button
                type="button"
                className={`upcoming-xrp-events-view-button${layout === "list" ? " is-active" : ""}`}
                aria-pressed={layout === "list"}
                aria-label="List view"
                onClick={() => selectLayout("list")}
              >
                <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <rect x="2" y="2.25" width="3.5" height="3.5" rx="0.6" fill="currentColor" />
                  <rect x="2" y="6.25" width="3.5" height="3.5" rx="0.6" fill="currentColor" />
                  <rect x="2" y="10.25" width="3.5" height="3.5" rx="0.6" fill="currentColor" />
                  <path
                    d="M7.5 4h6.5M7.5 8h6.5M7.5 12h6.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className="upcoming-xrp-events-sort" ref={sortRef}>
              <button
                type="button"
                className={`upcoming-xrp-events-sort-button${sortOpen ? " is-open" : ""}`}
                aria-haspopup="listbox"
                aria-expanded={sortOpen}
                aria-controls="upcoming-xrp-events-sort-menu"
                onClick={() => setSortOpen((open) => !open)}
              >
                Sort
                <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <path
                    d="M4 6l4 4 4-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {sortOpen ? (
                <ul
                  id="upcoming-xrp-events-sort-menu"
                  className="upcoming-xrp-events-sort-menu"
                  role="listbox"
                  aria-label="Sort ecosystem events"
                >
                  <li role="option" aria-selected={sortMode === "date"}>
                    <button type="button" onClick={() => selectSort("date")}>
                      Date
                    </button>
                  </li>
                  {ECOSYSTEM_EVENT_KIND_ORDER.map((kind) => (
                    <li key={kind} role="option" aria-selected={sortMode === kind}>
                      <button type="button" onClick={() => selectSort(kind)}>
                        {kind}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </div>
        {months.length === 0 ? (
          <p className="upcoming-xrp-events-empty">No events in this category.</p>
        ) : (
          <div className="upcoming-xrp-events-months">
            {months.map((month) => (
              <div key={month.key} className="upcoming-xrp-events-month">
                <h3 className="upcoming-xrp-events-month-title">{month.label}</h3>
                <div className="upcoming-xrp-events-grid" role="list">
                  {month.events.map((event) => (
                    <div key={event.id} className="upcoming-xrp-events-grid-item" role="listitem">
                      <EventCard
                        event={event}
                        onOpen={setSelectedEvent}
                        isOpen={selectedEvent?.id === event.id}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <EcosystemEventModal event={selectedEvent} onClose={closeEventModal} />
    </section>
  );
};

export default UpcomingXrpLedgerEvents;
